import { aboutStats, principles, testimonials, siteMeta } from '@/mocks/gossipData';

const ABOUT_IMG =
  'https://readdy.ai/api/search-image?query=Warm%20editorial%20illustration%20of%20a%20content%20editorial%20team%20around%20a%20desk%20with%20notebooks%20and%20coffee%2C%20golden%20amber%20and%20cream%20tones%2C%20friendly%20modern%20magazine%20style%2C%20soft%20flat%20layered%20illustration%20composition&width=760&height=560&seq=about-team&orientation=landscape';

export default function About() {
  return (
    <section id="about" className="scroll-mt-20 bg-secondary-50/70">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        {/* Intro */}
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-10">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-background-50 px-3 py-1 text-xs font-semibold tracking-wide text-primary-800 ring-1 ring-background-200">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              关于「昨日吃瓜」
            </div>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[2rem]">
              把散落的昨日热点，整理成一份好读的瓜报
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              「{siteMeta.brand}」是一家专注盘点的泛娱乐资讯站。我们不做搬运式转发，而是把刚过去的 24
              小时里分散在节目物料、官方口径与各平台讨论中的热点，按时间线与话题重新梳理，让它变成随时可以补看的"昨日瓜报"。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              我们相信吃瓜可以更轻量、更体面：轻量在于一页就能看明白来龙去脉，体面在于我们坚持先求证再表达、尊重不伤人的边界感。这也是为什么越来越多追瓜用户，把睡前看一遍当日瓜报当成了习惯。
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="#subscribe"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                订阅昨日瓜报 <i className="ri-arrow-right-line" />
              </a>
              <a
                href="#faq"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-foreground-200 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                常见问答 <i className="ri-question-line" />
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-2xl">
              <img src={ABOUT_IMG} alt="昨日吃瓜内容编辑团队工作场景插画" className="h-full w-full object-cover object-center" />
            </div>
            <div className="absolute -bottom-4 left-4 flex items-center gap-2 rounded-xl bg-background-50 px-4 py-2.5 shadow-soft">
              <i className="ri-verified-badge-fill text-lg text-primary-600" />
              <span className="text-xs font-semibold text-foreground-800">内容坚持先求证 · 再表达</span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-10 grid grid-cols-2 gap-3 rounded-2xl border border-background-200 bg-background-50 p-4 md:grid-cols-4 md:p-6">
          {aboutStats.map((s, i) => (
            <div key={i} className="flex flex-col items-center border-r border-background-200/70 px-2 text-center last:border-r-0">
              <span className="font-heading text-2xl font-bold text-primary-700 md:text-3xl">{s.value}</span>
              <span className="mt-1 text-xs text-foreground-500">{s.label}</span>
            </div>
          ))}
        </div>

        {/* Principles */}
        <div className="mt-10">
          <h3 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们坚持的四条内容原则</h3>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p, i) => (
              <div key={i} className="rounded-2xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-200">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className={`${p.icon} text-lg`} />
                </span>
                <h4 className="mt-3 font-heading text-base font-bold text-foreground-950">{p.title}</h4>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="mt-10">
          <div className="flex flex-col justify-between gap-2 md:flex-row md:items-end">
            <h3 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">追瓜用户怎么说</h3>
            <p className="text-xs text-foreground-500">来自通勤、午休与深夜补料的真实反馈</p>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <figure key={i} className="flex flex-col justify-between rounded-2xl border border-background-200 bg-background-50 p-5">
                <div>
                  <div className="flex gap-0.5 text-accent-500" aria-label="五星好评">
                    {Array.from({ length: 5 }).map((_, si) => (
                      <i key={si} className="ri-star-fill text-sm" />
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm leading-7 text-foreground-700">“{t.text}”</blockquote>
                </div>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                    {t.avatar}
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">{t.name}</div>
                    <div className="text-xs text-foreground-500">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}